import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Opening } from 'api-clients/monolith/models/Opening';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getOpenings } from '@/api-clients/get-openings';
import { Pagination } from '@/components/base/pagination';
import { Search } from '@/components/base/search';
import { DataTable } from '@/components/ui/data-table';
import { Typography } from '@/components/ui/typography';
import { useTableContext } from '@/contexts/table-context';
import { useDataTable } from '@/hooks/use-data-table';
import { queryKeys } from '@/query-keys';

import { useOpeningColumns } from './columns';
import { Filters } from './filters';
import { NoOpenings } from './no-openings';
import { RowsSelect } from './rows-select';

// The openings API (`ApiSelfServeV2::OpeningsController::FUNNELS_LIMIT`) caps
// per_page at 50, so offering the shared 100 option would silently be
// clamped server-side and mislead the user into thinking more rows loaded
// than actually did.
const OPENINGS_ROWS_PER_PAGE_OPTIONS = [25, 50];

export function OpeningsTable() {
  const intl = useIntl();
  const columns = useOpeningColumns();

  const {
    pagination: { pageIndex, pageSize },
    rowCount,
    setRowCount,
    searchQuery,
    setSearchQuery,
    filters: {
      locationIds,
      positionIds,
      isSelectedForSourcing,
      brandIds,
      active,
    },
    sorting,
  } = useTableContext();
  const sort = sorting[0];

  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      ...queryKeys.allOpenings,
      pageIndex,
      pageSize,
      searchQuery,
      locationIds,
      positionIds,
      isSelectedForSourcing,
      brandIds,
      active,
      sorting,
    ],
    queryFn: () =>
      getOpenings({
        page: pageIndex + 1,
        perPage: pageSize,
        locationIds,
        positionIds,
        isSelectedForSourcing,
        query: searchQuery,
        brandIds,
        active,
        sortBy: sort?.id,
        sortDirection: sort?.desc ? 'desc' : 'asc',
      }),
  });

  if (isError) {
    throw new Error('Error fetching openings');
  }

  // invalidateQueries: callback invoked by RowsSelect on mutation success (cross-component)
  const refetchOpenings = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.allOpenings,
    });
  }, [queryClient]);

  const tableRows = useMemo(() => data?.openings ?? [], [data]);

  useEffect(() => {
    setRowCount(data?.pagination?.total_count ?? 0);
  }, [data, setRowCount]);

  const { table } = useDataTable<Opening>({
    columns,
    data: tableRows,
    rowCount,
    externalIdKey: 'funnel_external_id',
  });

  // Remount Search when filters are cleared so its local input state resets.
  const [searchResetKey, setSearchResetKey] = useState(0);

  return (
    <>
      <Typography as="h2" variant="caption">
        <FormattedMessage
          defaultMessage="Openings"
          description="Table caption: count of rows is org job openings"
        />
      </Typography>
      <div className="flex justify-between gap-2">
        <Filters onCleared={() => setSearchResetKey(key => key + 1)} />
        <Search
          key={searchResetKey}
          initialValue={searchQuery}
          setSearchQuery={setSearchQuery}
          placeholder={intl.formatMessage({
            defaultMessage: 'Search by opening name...',
            description: 'Placeholder for the search input',
          })}
        />
      </div>
      <DataTable
        table={table}
        columns={columns}
        isLoading={isLoading}
        emptyState={<NoOpenings searchQuery={searchQuery} />}
      />
      <Pagination
        table={table}
        rowsPerPageOptions={OPENINGS_ROWS_PER_PAGE_OPTIONS}
      />
      <RowsSelect table={table} refetchOpenings={refetchOpenings} />
    </>
  );
}
